/**
 * Presentation interface (M2.5, FR-9.2/9.7/9.10).
 *
 * A read-only shared screen: the board + current prompt + timer + scores, and NO
 * controls. It follows game state automatically — when the host opens a clue it
 * shows the prompt; when the clue resolves it returns to the board (FR-9.7). The
 * host never navigates this screen; it reacts to the single WS state stream like
 * every other client. It never receives a clue `response` (FR-9.2).
 *
 * The screen binds to a SPECIFIC game via a join code (FR-9.10, #51): a shared link
 * `/presentation?code=<joinCode>` auto-attaches (the "open the TV" UX), and in prod
 * with no code we REQUIRE one on a code-entry screen rather than silently defaulting
 * to the demo game. In dev there is a single game, so the code is ignored and the
 * screen auto-attaches on load.
 */

import { gamePhaseOf } from '@jeopardy/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { requestSpectatorTicket, resolveGameByCode } from '../net/game-api';
import { IS_PROD_DEPLOY } from '../config';
import { useGameConnection } from '../net/use-game';
import { AppHeader } from '../ui/AppHeader';
import { FINAL_PERIL } from '../ui/brand';
import { Board } from '../ui/Board';
import { CluePanel } from '../ui/CluePanel';
import { ClueReveal } from '../ui/ClueReveal';
import { FinalJudgingAnswer } from '../ui/FinalJudgingAnswer';
import { FinalReveal } from '../ui/FinalReveal';
import { DisputePanel } from '../ui/DisputePanel';
import { GameOver } from '../ui/GameOver';
import { GenerationStatus, GenerationFailedBanner } from '../ui/GenerationStatus';
import { JoinShare } from '../ui/JoinShare';
import { Lobby } from '../ui/Lobby';
import { useFreshEvent } from '../ui/PowerUpHand';
import { PowerUpPlayedReveal, PowerUpThreatStrip } from '../ui/PowerUpTv';
import { Scoreboard } from '../ui/Scoreboard';
import { TimerView } from '../ui/TimerView';
import { useClueReader } from '../voice/use-clue-reader';
import { useDocumentHead } from '../seo/useDocumentHead';
import { headModelFor } from '../seo/route-seo';
import {
  categoryNameForClue,
  clueReveal,
  currentClue,
  finalJudgingAnswer,
  finalRevealResolvesAt,
  finalRevealResponse,
  isClueOpen,
  revealedResponse,
} from '../game/selectors';
import {
  answerBeingRead,
  clueBeingRead,
  finalAnswerBeingRead,
  isJudging,
} from '../game/voice-selectors';

export function PresentationPage(): React.JSX.Element {
  // Per-route SEO (#241, FR-15.4): self-canonical but `noindex` — the shared-screen TV
  // surface is an app view, not a search landing.
  useDocumentHead(headModelFor('/presentation'));
  const [ticket, setTicket] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The join code identifying WHICH game to present, seeded from `?code=` in a shared
  // link (`/presentation?code=…`). In dev the code is ignored (a single game).
  const [code, setCode] = useState(() => {
    if (typeof location === 'undefined') return '';
    return new URLSearchParams(location.search).get('code') ?? '';
  });
  // Whether an attach (resolve code → mint ticket) is in flight. Initialized true when
  // there is something to attach with on load — dev (single game) or prod WITH a code —
  // so the first paint shows "Connecting…", not a flash of the code-entry form.
  const [attaching, setAttaching] = useState(!IS_PROD_DEPLOY || code.trim().length > 0);

  const open = useCallback(async (joinCode: string) => {
    setError(null);
    setAttaching(true);
    try {
      // In prod, resolve the host's join code → the specific gameId so the screen
      // presents THAT game, never the demo game (FR-9.10, #51). Dev has one game, so
      // the code is ignored and the ticket mints against it directly.
      const trimmed = joinCode.trim();
      const gameId =
        IS_PROD_DEPLOY && trimmed ? await resolveGameByCode(trimmed) : undefined;
      const t = await requestSpectatorTicket('presentation', gameId);
      setTicket(t.ticket);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not open presentation');
    } finally {
      setAttaching(false);
    }
  }, []);

  // Auto-attach once on load: dev (no code) and prod with a shared `?code=` link both
  // attach immediately. Prod with no code falls through to the code-entry screen below.
  // Keyed off the code present at MOUNT (`initialCode`), never the field the user later
  // types into — so typing in the form doesn't auto-submit a partial code.
  const initialCode = useRef(code).current;
  const attempted = useRef(false);
  useEffect(() => {
    if (attempted.current || ticket) return;
    if (!IS_PROD_DEPLOY || initialCode.trim()) {
      attempted.current = true;
      void open(initialCode);
    }
  }, [ticket, open, initialCode]);

  const { snapshot, status, send } = useGameConnection(ticket);
  const state = snapshot.state;
  const clue = currentClue(state);
  const clueOpen = isClueOpen(state);
  const reveal = clueReveal(state);
  const awardedName =
    reveal?.awardedPlayerId !== undefined
      ? state?.players.find((p) => p.id === reveal.awardedPlayerId)?.name
      : undefined;

  // Auto-host (#91): the shared screen is the natural speaker — read the clue aloud
  // while it's in READING, then report `read_complete`. A no-op in HUMAN games.
  useClueReader({ state, send });

  // #232 (Game Night only): transient TV juice. The attributed "PLAYER played CARD" slam
  // fires off the broadcast `powerup_played`; the ✦ "new card" glow highlights whoever
  // just gained a card. Both no-op in Classic (no `powerups`). Hooks run every render
  // (before the code-entry early return below).
  const powerupsOn = Boolean(state?.jeopardy.powerups);
  const freshPlayed = useFreshEvent(powerupsOn ? snapshot.lastPowerupPlayed : null);
  const freshGrant = useFreshEvent(powerupsOn ? snapshot.lastPowerupGranted : null);
  const playedByName =
    freshPlayed !== null
      ? (state?.players.find((p) => p.id === freshPlayed.playerId)?.name ?? null)
      : null;

  // Prod, not attached to a game and not mid-attach → REQUIRE a join code before
  // presenting anything (no silent demo default, FR-9.10). A failed auto-attach lands
  // here too (a bad `?code=` leaves `attaching` false + an error) so it's correctable.
  // Dev never reaches this branch (single game, always auto-attaches).
  if (IS_PROD_DEPLOY && !ticket && !attaching) {
    return (
      <div className="app-shell">
        <AppHeader role="Presentation" status={status} />
        <form
          className="join-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (code.trim()) void open(code);
          }}
        >
          <h1 className="app-title">Open the presentation screen</h1>
          <p className="muted">Enter the host&rsquo;s join code to present that game.</p>
          <input
            aria-label="Join code"
            data-testid="presentation-code"
            placeholder="Join code (from the host)"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoFocus
          />
          <button
            type="submit"
            className="btn btn--primary"
            data-testid="presentation-code-submit"
            disabled={code.trim().length === 0}
          >
            Open presentation
          </button>
          {error && (
            <div className="error-text" data-testid="presentation-error">
              {error}
            </div>
          )}
        </form>
      </div>
    );
  }

  return (
    <div className="app-shell">
      {/* Passive cast screen — home nav is immediate (no active-game guard). */}
      <AppHeader role="Presentation" status={status} />

      {error && <div className="error-text">{error}</div>}
      {!snapshot.hasState && !error && <p className="muted">Connecting…</p>}

      {state &&
        (gamePhaseOf(state) === 'LOBBY' || gamePhaseOf(state) === 'GENERATING') && (
          <div className="stack">
            {/* The shared-screen "grab your phone and scan" on-ramp (FR-2.9). Shown in
              the pre-play lobby, where joining players look. Prod only — dev runs a
              single implicit game with no join code. */}
            {IS_PROD_DEPLOY && state.joinCode && (
              <JoinShare joinCode={state.joinCode} variant="feature" />
            )}
            {/* Generation failed (FR-9.9): the shared screen shows the read-only reason
              so the room knows what happened (only the host can retry). */}
            {snapshot.lastGenerationFailure && gamePhaseOf(state) === 'LOBBY' && (
              <GenerationFailedBanner
                code={snapshot.lastGenerationFailure.code}
                message={snapshot.lastGenerationFailure.message}
              />
            )}
            <Lobby players={state.players} mode={state.mode} lobby={state.lobby} />
            {gamePhaseOf(state) === 'GENERATING' && state.generation && (
              <GenerationStatus generation={state.generation} />
            )}
          </div>
        )}

      {state && gamePhaseOf(state) !== 'LOBBY' && gamePhaseOf(state) !== 'GENERATING' && (
        <div className="stack">
          {/* #232: the attributed card-played slam (mock 06/08), public info only. */}
          {powerupsOn && freshPlayed && playedByName && (
            <PowerUpPlayedReveal
              playerName={playedByName}
              powerup={freshPlayed.powerup}
            />
          )}
          {gamePhaseOf(state) === 'GAME_OVER' ? (
            <GameOver
              players={state.players}
              gameId={IS_PROD_DEPLOY ? state.gameId : undefined}
              finalOutcome={state.jeopardy.finalOutcome}
            />
          ) : gamePhaseOf(state) === 'FINAL_WAGER' ||
            gamePhaseOf(state) === 'FINAL_CLUE' ||
            gamePhaseOf(state) === 'FINAL_JUDGING' ||
            gamePhaseOf(state) === 'FINAL_REVEAL' ? (
            <div className="stack" data-testid="final-panel">
              <div className="clue-panel">
                <div className="clue-panel__value">
                  {FINAL_PERIL} — {state.jeopardy.final?.category}
                </div>
                <div className="clue-panel__prompt">
                  {/* Prompt is server-withheld until FINAL_CLUE (empty before). */}
                  {state.jeopardy.packSnapshot.finalJeopardy.prompt ||
                    (gamePhaseOf(state) === 'FINAL_WAGER'
                      ? 'Players are wagering…'
                      : 'Get ready…')}
                </div>
                {state.timer.clueId && <TimerView timer={state.timer} />}
              </div>
              {/* #344 (FR-6.7): the currently-rated contestant's proposed answer, shown to
                  the room above the correct-response band during FINAL_JUDGING. */}
              {finalJudgingAnswer(state) && (
                <FinalJudgingAnswer
                  name={finalJudgingAnswer(state)!.name}
                  answer={finalJudgingAnswer(state)!.answer}
                />
              )}
              {/* #205 (FR-6.6, amended by #344): reveal the correct response to the room
                  once the reveal fires (first-correct ruling, or the all-wrong linger),
                  held for suspense until then. Flips in (#344, D344.10). */}
              {finalRevealResponse(state) && (
                <>
                  <div className="final-reveal-flip">
                    <FinalReveal
                      response={finalRevealResponse(state)!}
                      category={state.jeopardy.final?.category}
                      resolvesAtEpochMs={finalRevealResolvesAt(state)}
                    />
                  </div>
                  {/* Auto-host (#205): the shared screen reads the answer aloud. */}
                  {finalAnswerBeingRead(state) && (
                    <p className="muted" data-testid="final-answer-reading">
                      Reading the answer aloud…
                    </p>
                  )}
                </>
              )}
            </div>
          ) : reveal && clue ? (
            /* #192 (FR-4.10): the clue resolved — reveal the answer (unanswered) or
               the award cue (correct) for the brief hold, then the board returns. */
            <>
              <ClueReveal
                reveal={reveal}
                response={revealedResponse(state)}
                categoryName={categoryNameForClue(state, clue.id)}
                value={clue.value}
                awardedPlayerName={awardedName}
              />
              {/* Auto-host (#192): the shared screen reads the revealed answer aloud. */}
              {answerBeingRead(state) === clue.id && (
                <p className="muted" data-testid="answer-reading">
                  Reading the answer aloud…
                </p>
              )}
            </>
          ) : clueOpen && clue ? (
            <>
              <CluePanel
                value={clue.value}
                prompt={clue.prompt}
                categoryName={categoryNameForClue(state, clue.id)}
              />
              {state.timer.clueId && <TimerView timer={state.timer} />}
              {/* Auto-host (#91): reading-aloud + judging status for the room. */}
              {clueBeingRead(state) === clue.id && (
                <p className="muted" data-testid="clue-reading">
                  Reading the clue aloud…
                </p>
              )}
              {isJudging(state) && (
                <p className="muted" data-testid="judging-indicator">
                  Judging the answer…
                </p>
              )}
            </>
          ) : (
            <Board
              board={state.jeopardy.board}
              cluesById={state.jeopardy.packSnapshot.cluesById}
            />
          )}
          {/* Auto-host (#91, D8): read-only dispute status for the shared screen (no
              playerId → tally only, no vote controls). */}
          {gamePhaseOf(state) !== 'GAME_OVER' && (
            <DisputePanel state={state} playerId={null} send={send} />
          )}
          <Scoreboard
            players={state.players}
            boardControlPlayerId={state.jeopardy.boardControlPlayerId}
            clueState={state.jeopardy.clueState}
          />
          {/* #232: a persistent "Cards in hand" threat strip under the board (mock 07/12).
              Shows the capability + a fading ✦ on a fresh grant — never the reason. */}
          {powerupsOn && gamePhaseOf(state) !== 'GAME_OVER' && (
            <PowerUpThreatStrip
              players={state.players}
              newCardPlayerId={freshGrant?.playerId}
            />
          )}
        </div>
      )}
    </div>
  );
}
